import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { Loader2, Plus, Trash2, FileDown, Mail, Workflow, ListPlus, Share2, RefreshCw, Rows3 } from "lucide-react";
import ProcessFlowCanvas, { TYPE_META } from "@/components/ProcessFlowCanvas";
import ProcessSwimlanes from "@/components/ProcessSwimlanes";
import ProcessShareDialog from "@/components/ProcessShareDialog";

function ProcessToPlanDialog({ proc, open, onOpenChange }) {
  const [phases, setPhases] = useState([]);
  const [phase, setPhase] = useState("__new__");
  const [startDate, setStartDate] = useState(new Date().toISOString().slice(0, 10));
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    api.get(`/projects/${proc.project_id}`).then(({ data }) => setPhases(data.phases || [])).catch(() => setPhases([]));
  }, [open, proc.project_id]);

  const build = async () => {
    setBusy(true);
    try {
      const isNew = phase === "__new__";
      const { data } = await api.post(`/processes/${proc.id}/to-plan`, {
        phase: isNew ? "" : phase, new_phase: isNew, start_date: startDate || null,
      });
      toast.success(`${data.created} tasks created in phase "${data.phase}" — opening the plan`);
      onOpenChange(false);
      setTimeout(() => window.location.assign(`/projects/${data.project_id}?tab=plan`), 600);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't convert the process");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !busy && onOpenChange(v)}>
      <DialogContent className="sm:max-w-md" data-testid="process-to-plan-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><ListPlus className="h-4 w-4 text-primary" /> Turn "{proc.name}" into plan tasks</DialogTitle></DialogHeader>
        <p className="text-sm text-muted-foreground -mt-2">Each step becomes a scheduled, dependency-chained task; decisions become checkpoints with their yes/no branches; the end becomes a milestone.</p>
        <div className="space-y-4">
          <div>
            <Label>Target phase</Label>
            <Select value={phase} onValueChange={setPhase}>
              <SelectTrigger className="mt-1.5" data-testid="process-to-plan-phase"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__new__">➕ New phase: "{proc.name.slice(0, 40)}"</SelectItem>
                {phases.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Start date</Label>
            <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="mt-1.5" data-testid="process-to-plan-start" />
          </div>
          <Button className="w-full gap-2" onClick={build} disabled={busy} data-testid="process-to-plan-build">
            {busy ? <><Loader2 className="h-4 w-4 animate-spin" /> Creating tasks…</> : <><ListPlus className="h-4 w-4" /> Create the tasks</>}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function ProcessView({ projectId }) {
  const [processes, setProcesses] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [selected, setSelected] = useState(null);
  const [newOpen, setNewOpen] = useState(false);

  const load = async (keepId) => {
    try {
      const [p, t] = await Promise.all([api.get(`/projects/${projectId}/processes`), api.get(`/projects/${projectId}/tasks`)]);
      setProcesses(p.data);
      setTasks(t.data);
      setSelected((prev) => {
        const id = keepId || prev;
        return p.data.some((x) => x.id === id) ? id : p.data[0]?.id || null;
      });
    } catch (e) {
      toast.error("Could not load processes");
      setProcesses([]);
    }
  };
  useEffect(() => { load(); }, [projectId]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!processes) return <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;
  const proc = processes.find((p) => p.id === selected);

  return (
    <div className="space-y-5" data-testid="process-view">
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex flex-wrap gap-1.5 flex-1 min-w-0" data-testid="process-tabs">
          {processes.map((p) => (
            <button key={p.id} onClick={() => setSelected(p.id)}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors max-w-[220px] truncate ${p.id === selected ? "bg-primary text-primary-foreground border-primary" : "border-border text-muted-foreground hover:text-foreground"}`}
              data-testid={`process-tab-${p.id}`}>
              <Workflow className="h-3 w-3 inline mr-1 -mt-0.5" />{p.name}
              {p.plan_changed && <span className="ml-1.5 inline-block h-2 w-2 rounded-full bg-amber-500 align-middle" title="The plan has changed since this diagram was mapped" data-testid={`plan-changed-dot-${p.id}`} />}
            </button>
          ))}
          {processes.length === 0 && <p className="text-sm text-muted-foreground py-1">No processes yet — design your first process flow.</p>}
        </div>
        <Button size="sm" className="gap-1.5 shrink-0" onClick={() => setNewOpen(true)} data-testid="new-process-button"><Plus className="h-3.5 w-3.5" /> New process</Button>
      </div>

      {proc && <ProcessEditor key={proc.id} proc={proc} tasks={tasks} onChanged={load} />}

      <NewProcessDialog open={newOpen} onOpenChange={setNewOpen} projectId={projectId}
        onCreated={(p) => { setNewOpen(false); load(p.id); }} />
    </div>
  );
}

function ProcessEditor({ proc, tasks, onChanged }) {
  const [steps, setSteps] = useState(proc.steps || []);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [emailing, setEmailing] = useState(false);
  const [toPlanOpen, setToPlanOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [viewMode, setViewMode] = useState("flow");
  const [editStep, setEditStep] = useState(null);
  const [editBranch, setEditBranch] = useState(null); // {stepId, branch, item}
  const taskById = Object.fromEntries(tasks.map((t) => [t.id, t]));

  const mutate = (next) => { setSteps(next); setDirty(true); };
  const move = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= steps.length) return;
    const next = [...steps];
    [next[i], next[j]] = [next[j], next[i]];
    mutate(next);
  };
  const removeStep = (i) => mutate(steps.filter((_, idx) => idx !== i));
  const toggleParallel = (i) => mutate(steps.map((s, idx) => (idx === i ? { ...s, parallel_with_prev: !s.parallel_with_prev } : s)));
  const dropParallel = (from, to) => {
    const src = steps[from], tgt = steps[to];
    if (!src || !tgt || src.type !== "step" || tgt.type !== "step") return;
    const next = steps.filter((_, idx) => idx !== from);
    const tIdx = next.findIndex((x) => x.id === tgt.id);
    let start = tIdx;
    while (start > 0 && next[start].parallel_with_prev && next[start - 1].type === "step") start -= 1;
    let size = 1, k = start + 1;
    while (k < next.length && next[k].type === "step" && next[k].parallel_with_prev) { size += 1; k += 1; }
    if (size >= 3) { toast.error("That row already has 3 parallel steps — the maximum for a readable diagram"); return; }
    next.splice(tIdx + 1, 0, { ...src, parallel_with_prev: true });
    mutate(next);
    toast.success(`"${src.title}" now runs in parallel with "${tgt.title}" — remember to save`);
  };
  const upsertBranchStep = (stepId, branch, item) =>
    mutate(steps.map((s) => {
      if (s.id !== stepId) return s;
      const list = s[branch] || [];
      return { ...s, [branch]: list.some((b) => b.id === item.id) ? list.map((b) => (b.id === item.id ? item : b)) : [...list, item] };
    }));
  const removeBranchStep = (stepId, branch, id) =>
    mutate(steps.map((s) => (s.id === stepId ? { ...s, [branch]: (s[branch] || []).filter((b) => b.id !== id) } : s)));
  const moveBranchStep = (stepId, branch, i, dir) =>
    mutate(steps.map((s) => {
      if (s.id !== stepId) return s;
      const list = [...(s[branch] || [])];
      const j = i + dir;
      if (j < 0 || j >= list.length) return s;
      [list[i], list[j]] = [list[j], list[i]];
      return { ...s, [branch]: list };
    }));
  const upsertStep = (step) => {
    const idx = steps.findIndex((s) => s.id === step.id);
    if (idx >= 0) mutate(steps.map((s, i) => (i === idx ? step : s)));
    else {
      const endIdx = steps.findIndex((s) => s.type === "end");
      const next = [...steps];
      next.splice(endIdx >= 0 ? endIdx : steps.length, 0, step);
      mutate(next);
    }
    setEditStep(null);
  };

  const editors = {
    move,
    edit: (s) => setEditStep(s),
    remove: removeStep,
    toggleParallel,
    dropParallel,
    canParallel: (i) => i > 0 && steps[i].type === "step" && steps[i - 1].type === "step",
    branchAdd: (stepId, branch) => setEditBranch({ stepId, branch, item: { id: crypto.randomUUID(), title: "", description: "", owner: "", linked_task_ids: [] } }),
    branchEdit: (stepId, branch, item) => setEditBranch({ stepId, branch, item }),
    branchRemove: removeBranchStep,
    branchMove: moveBranchStep,
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/processes/${proc.id}`, { steps });
      setDirty(false);
      toast.success("Process saved");
      onChanged(proc.id);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const downloadPdf = async () => {
    setDownloading(true);
    try {
      if (dirty) { await api.put(`/processes/${proc.id}`, { steps }); setDirty(false); }
      const { data } = await api.get(`/processes/${proc.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `${proc.name.replace(/[^a-z0-9-_ ]/gi, "")}-process.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Could not generate the PDF");
    } finally { setDownloading(false); }
  };

  const emailPdf = async () => {
    setEmailing(true);
    try {
      if (dirty) { await api.put(`/processes/${proc.id}`, { steps }); setDirty(false); }
      const { data } = await api.post(`/processes/${proc.id}/email-pdf`);
      if (data.sent) toast.success(`PDF copy sent to ${data.to}`);
      else toast.error(data.error || "Email could not be sent");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setEmailing(false); }
  };

  const linkedToPlan = proc.source_type === "plan" || /^(project plan|phase ')/.test(proc.source || "");
  const refreshFromPlan = async () => {
    if (!window.confirm("Maestro will re-map this diagram from the current plan and replace its steps. Continue?")) return;
    setRefreshing(true);
    try {
      await api.post(`/processes/${proc.id}/refresh-from-plan`, {}, { timeout: 120000 });
      toast.success("Diagram refreshed from the latest plan");
      onChanged(proc.id);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't refresh from the plan");
    } finally { setRefreshing(false); }
  };

  const deleteProcess = async () => {
    if (!window.confirm(`Delete the "${proc.name}" process?`)) return;
    try {
      await api.delete(`/processes/${proc.id}`);
      toast.success("Process deleted");
      onChanged();
    } catch (e) { toast.error("Could not delete process"); }
  };

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex-1 min-w-0">
          <h3 className="font-display text-lg font-bold truncate">{proc.name}</h3>
          {proc.description && <p className="text-sm text-muted-foreground truncate">{proc.description}</p>}
        </div>
        <Button size="sm" variant="outline" className="gap-1.5" onClick={() => setEditStep({ id: crypto.randomUUID(), title: "", type: "step", description: "", owner: "", linked_task_ids: [] })} data-testid="add-step-button"><Plus className="h-3.5 w-3.5" /> Add step</Button>
        <Button size="sm" variant="outline" className="gap-1.5" onClick={() => setToPlanOpen(true)} data-testid="process-to-plan-button"><ListPlus className="h-3.5 w-3.5" /> Make it a plan</Button>
        {linkedToPlan && (
          <Button size="sm" variant="outline" className={`gap-1.5 ${proc.plan_changed ? "border-amber-400 text-amber-700 hover:bg-amber-50" : ""}`} onClick={refreshFromPlan} disabled={refreshing} title="Re-map this diagram from the current project plan" data-testid="refresh-from-plan-button">
            {refreshing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />} Refresh from plan
          </Button>
        )}
        {linkedToPlan && proc.plan_changed && (
          <span className="inline-flex items-center gap-1 text-[11px] font-bold text-amber-700 bg-amber-50 border border-amber-300 rounded-full px-2.5 py-1" data-testid="plan-changed-badge">
            Plan has changed — refresh recommended
          </span>
        )}
        <Button size="sm" variant="outline" className="gap-1.5" onClick={async () => { if (dirty) { try { await api.put(`/processes/${proc.id}`, { steps }); setDirty(false); } catch (e) { toast.error("Couldn't save your latest edits"); return; } } setShareOpen(true); }} data-testid="share-process-button"><Share2 className="h-3.5 w-3.5" /> Share</Button>
        <Button size="sm" variant="outline" className="gap-1.5" onClick={downloadPdf} disabled={downloading} data-testid="download-pdf-button">
          {downloading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <FileDown className="h-3.5 w-3.5" />} Download PDF
        </Button>
        <Button size="sm" variant="outline" className="gap-1.5" onClick={emailPdf} disabled={emailing} data-testid="email-pdf-button">
          {emailing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Mail className="h-3.5 w-3.5" />} Email me a copy
        </Button>
        {dirty && <Button size="sm" onClick={save} disabled={saving} data-testid="save-process-button">{saving && <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" />} Save</Button>}
        <Button size="sm" variant="ghost" onClick={deleteProcess} data-testid="delete-process-button"><Trash2 className="h-3.5 w-3.5 text-muted-foreground" /></Button>
        <ProcessToPlanDialog proc={proc} open={toPlanOpen} onOpenChange={setToPlanOpen} />
        <ProcessShareDialog proc={proc} open={shareOpen} onOpenChange={setShareOpen} />
      </div>

      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-1 rounded-lg border border-border p-1 w-fit" data-testid="process-view-toggle">
          <button type="button" onClick={() => setViewMode("flow")} className={`px-3 py-1.5 text-xs font-semibold rounded-md transition-colors inline-flex items-center gap-1.5 ${viewMode === "flow" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`} data-testid="process-view-flow"><Workflow className="h-3.5 w-3.5" /> Flow</button>
          <button type="button" onClick={() => setViewMode("lanes")} className={`px-3 py-1.5 text-xs font-semibold rounded-md transition-colors inline-flex items-center gap-1.5 ${viewMode === "lanes" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`} data-testid="process-view-swimlanes"><Rows3 className="h-3.5 w-3.5" /> Swimlanes (by owner)</button>
        </div>
        {viewMode === "flow" && <p className="text-[11px] text-muted-foreground" data-testid="drag-parallel-hint">Tip: drag a step onto another to run them side-by-side (in parallel)</p>}
      </div>

      {viewMode === "flow"
        ? <ProcessFlowCanvas steps={steps} taskById={taskById} editors={editors} />
        : <ProcessSwimlanes steps={steps} />}

      {editStep && <StepDialog step={editStep} tasks={tasks} onClose={() => setEditStep(null)} onSave={upsertStep} />}
      {editBranch && (
        <StepDialog step={editBranch.item} tasks={tasks} branchLabel={editBranch.branch === "yes_steps" ? "Yes" : "No"}
                    onClose={() => setEditBranch(null)}
                    onSave={(item) => { const { type, ...rest } = item; upsertBranchStep(editBranch.stepId, editBranch.branch, rest); setEditBranch(null); }} />
      )}
    </div>
  );
}

function StepDialog({ step, tasks, onClose, onSave, branchLabel }) {
  const [title, setTitle] = useState(step.title);
  const [type, setType] = useState(step.type);
  const [description, setDescription] = useState(step.description || "");
  const [owner, setOwner] = useState(step.owner || "");
  const [linked, setLinked] = useState(step.linked_task_ids || []);
  const ownerOptions = [...new Set(tasks.flatMap((t) => [t.assignee, t.responsible, t.accountable].flatMap((v) => (v || "").split(",")).map((x) => x.trim()).filter(Boolean)))].sort();

  const toggleTask = (id) => setLinked((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-lg" data-testid="step-dialog">
        <DialogHeader><DialogTitle>{branchLabel ? `${step.title ? "Edit" : "Add"} "${branchLabel}" path step` : step.title ? "Edit step" : "Add step"}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Title</Label>
              <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Review with sponsor" data-testid="step-title-input" />
            </div>
            {!branchLabel && (
              <div className="space-y-1.5">
                <Label>Type</Label>
                <Select value={type} onValueChange={setType}>
                  <SelectTrigger data-testid="step-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(TYPE_META).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
          {!branchLabel && type === "decision" && (
            <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2" data-testid="decision-hint">
              Decisions render as a diamond with a <b>Yes</b> and a <b>No</b> path — after saving, use the “+ Add Yes step” / “+ Add No step” buttons under the diamond to detail each path.
            </p>
          )}
          <div className="space-y-1.5">
            <Label>Owner (optional)</Label>
            <Input value={owner} onChange={(e) => setOwner(e.target.value)} placeholder="Who runs this step?" list="step-owner-suggestions" data-testid="step-owner-input" />
            <datalist id="step-owner-suggestions">
              {ownerOptions.map((n) => <option key={n} value={n} />)}
            </datalist>
            {ownerOptions.length > 0 && (
              <div className="flex flex-wrap gap-1 pt-0.5" data-testid="step-owner-suggestion-chips">
                {ownerOptions.filter((n) => n !== owner).slice(0, 6).map((n) => (
                  <button key={n} type="button" onClick={() => setOwner(n)} className="text-[10px] font-semibold px-2 py-0.5 rounded-full border border-border text-muted-foreground hover:border-primary hover:text-primary transition-colors" data-testid={`step-owner-chip-${n.replace(/\s/g, "-")}`}>{n}</button>
                ))}
              </div>
            )}
          </div>
          <div className="space-y-1.5">
            <Label>Description (optional)</Label>
            <Textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={3} data-testid="step-description-input" />
          </div>
          {tasks.length > 0 && (
            <div className="space-y-1.5">
              <Label>Linked tasks</Label>
              <div className="max-h-40 overflow-y-auto border border-border rounded-md p-2 space-y-1.5">
                {tasks.map((t) => (
                  <label key={t.id} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-secondary/50 rounded px-1.5 py-1">
                    <Checkbox checked={linked.includes(t.id)} onCheckedChange={() => toggleTask(t.id)} data-testid={`step-task-${t.id}`} />
                    <span className="truncate">{t.title}</span>
                  </label>
                ))}
              </div>
            </div>
          )}
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={onClose}>Cancel</Button>
            <Button onClick={() => { if (!title.trim()) return toast.error("Step needs a title"); onSave({ ...step, title: title.trim(), type, description, owner, linked_task_ids: linked }); }} data-testid="step-save-button">Save step</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function NewProcessDialog({ open, onOpenChange, projectId, onCreated }) {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [saving, setSaving] = useState(false);

  const create = async () => {
    if (!name.trim()) return toast.error("Give the process a name");
    setSaving(true);
    try {
      const { data } = await api.post(`/projects/${projectId}/processes`, { name: name.trim(), description: description.trim() });
      setName(""); setDescription("");
      onCreated(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="new-process-dialog">
        <DialogHeader><DialogTitle>New process</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label>Name</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Change request approval" data-testid="process-name-input" />
          </div>
          <div className="space-y-1.5">
            <Label>Description (optional)</Label>
            <Textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={2} data-testid="process-description-input" />
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button onClick={create} disabled={saving} data-testid="create-process-submit">{saving && <Loader2 className="h-4 w-4 animate-spin mr-1.5" />} Create process</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
